import { resumeSignupHandoff } from '@common/metrics/journey/journeys/signup';
import { INDUSTRIES, PROFESSIONS, UserIndustry, UserProfession } from '@common/onboarding/userConstants';
import { HUB_URL } from '@common/packageHub/constants';
import { getSqlIDEUrl } from '@common/url';
import { OnboardingAnalytics } from '@common/user-analytics/events';
import { ArrowLeftIcon, ArrowRightIcon } from '@prophecy/icons';
import { EventName, EventType, metrics } from '@prophecy/metrics';
import { theme } from '@prophecy/theme';
import { DialogSize } from '@prophecy/ui-v3/Dialog/types';
import { Button, Centered, Dialog, Spinner, Stack, Text, toast } from '@prophecy/ui-v3/index';
import { computeArticleForText } from '@prophecy/utils/string';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { useMixPanel } from '../../context/mixpanel/context';
import { useGraphQlMutation } from '../../data/util';
import { useOnboardingUrl } from './hooks';
import { OnboardingQuestion } from './OnboardingQuestion';
import { CreateOnboardingAnalystProject, UpdateUserOnboardingDetails } from './query';
import { TypingText } from './TypingText';

const STEPS = ['profession', 'industry'] as const;

export const Onboarding = () => {
  const { mutateAsync: createOnboardingAnalystProject } = useGraphQlMutation(CreateOnboardingAnalystProject);
  const { mutateAsync: updateUserOnboardingDetails } = useGraphQlMutation(UpdateUserOnboardingDetails);

  const [profession, setProfession] = useState<UserProfession | ''>('');
  const [industry, setIndustry] = useState<UserIndustry | ''>('');
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [inProgress, setInProgress] = useState<boolean>(false);

  const stepType = STEPS[currentStep];

  const isLastStep = currentStep === STEPS.length - 1;
  const isFirstStep = currentStep === 0;

  const navigate = useNavigate();
  const { track } = useMixPanel();

  const globalConfigManager = useGlobalVariables();

  const { pending, url: onboardingUrl } = useOnboardingUrl();

  const [showLoading, setShowLoading] = useState<boolean>(true);

  // No-op unless this page was actually reached via a fresh signup — see signup.ts.
  useEffect(() => {
    resumeSignupHandoff();
  }, []);

  useEffect(() => {
    if (pending) {
      return;
    }
    if (onboardingUrl) {
      navigate(onboardingUrl);
      return;
    }
    setShowLoading(false);
  }, [onboardingUrl, navigate, pending]);

  const isCurrentStepComplete = () => {
    if (stepType === 'profession') {
      return Boolean(profession);
    }
    if (stepType === 'industry') {
      return Boolean(industry);
    }
    return false;
  };

  const handleComplete = async () => {
    if (!profession || !industry) {
      return;
    }
    setInProgress(true);

    track(
      OnboardingAnalytics.onboarding_questionnaire.events.onboarding_questionnaire_complete,
      {
        onboarding_questionnaire_complete: true,
        onboarding_questionnaire_complete_profession: profession,
        onboarding_questionnaire_complete_industry: industry
      },
      OnboardingAnalytics.onboarding_questionnaire.event_category
    );
    metrics.track({
      eventName: EventName.ui.onboarding.questionnaire.end,
      eventType: EventType.userAction,
      payload: {
        onboardingQuestionnaireComplete: true,
        onboardingQuestionnaireCompleteProfession: profession,
        onboardingQuestionnaireCompleteIndustry: industry
      }
    });
    try {
      // Update the user onboarding details irrespective of the redirect to spark or sql
      await updateUserOnboardingDetails({
        profession,
        industry
      });

      globalConfigManager.setUserOnboardingDetails({ profession, industry });

      // Redirect to template hub if the industry is OTHER or HOSPITALITY
      if (industry === UserIndustry.OTHER || industry === UserIndustry.HOSPITALITY) {
        navigate(HUB_URL.TEMPLATE);
        return;
      }

      const analystProjectResult = await createOnboardingAnalystProject({ industry });
      globalConfigManager.setAnalystProjectId(analystProjectResult.createOnboardingAnalystProject._id);
      const sqlOnboardingUrl = getSqlIDEUrl({
        uid: analystProjectResult.createOnboardingAnalystProject._id,
        onboarding: 'true'
      });
      navigate(sqlOnboardingUrl);
    } catch (error) {
      toast.error({
        content: 'Error creating onboarding project',
        'data-test-id': 'onboarding-error-toast'
      });
    } finally {
      setInProgress(false);
    }
  };

  const isStepComplete = isCurrentStepComplete();
  const articleForProfession = computeArticleForText(profession);

  // If the onboarding URL is not null, the user is already onboarded and we don't need to show the onboarding dialog.
  if (onboardingUrl) {
    return;
  }
  if (showLoading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }
  return (
    <Dialog
      open={true}
      rootClassName='onboarding-dialog'
      size={DialogSize.xl}
      closeButton={null}
      okButton={null}
      closable={false}
      data-test-id='onboarding-dialog'>
      <Centered>
        <Stack direction='vertical' gap={theme.spaces.x20} width='100%'>
          <Stack direction='vertical' gap={theme.spaces.x2} align='center' alignY='center'>
            <Title> Tell us about yourself</Title>
            <SubTitle>Two quick questions to personalize your experience</SubTitle>
          </Stack>
          <Stack alignY='center' padding={`${theme.spaces.x20} 0`}>
            <Text level='lg'>
              I am {articleForProfession} <TypingText text={profession} /> working in the <TypingText text={industry} />{' '}
              industry
            </Text>
          </Stack>
          {stepType === 'profession' && (
            <OnboardingQuestion
              question='Select your Profession'
              answer={profession}
              options={PROFESSIONS}
              onChange={(value) => {
                setProfession(value as UserProfession);
                setCurrentStep(currentStep + 1);
              }}
            />
          )}
          {stepType === 'industry' && (
            <OnboardingQuestion
              question='Select your Industry'
              answer={industry}
              options={INDUSTRIES}
              onChange={(value) => {
                setIndustry(value as UserIndustry);
              }}
            />
          )}

          <Stack
            direction='horizontal'
            gap={theme.spaces.x8}
            width='100%'
            align={isFirstStep ? 'end' : 'space-between'}>
            {!isFirstStep && (
              <NavigationButton
                variant='secondaryGray'
                onClick={() => setCurrentStep(currentStep - 1)}
                data-test-id='onboarding-previous-button'>
                <ArrowLeftIcon type={'default'} />
              </NavigationButton>
            )}

            {isLastStep ? (
              <SubmitButton
                variant='primary'
                disabled={!isStepComplete || inProgress}
                onClick={handleComplete}
                loading={inProgress}
                data-test-id='onboarding-complete-button'>
                Complete
              </SubmitButton>
            ) : (
              <NavigationButton
                variant='secondaryGray'
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={!isStepComplete}
                data-test-id='onboarding-next-button'>
                <ArrowRightIcon type={'default'} />
              </NavigationButton>
            )}
          </Stack>
        </Stack>
      </Centered>
    </Dialog>
  );
};

const Title = styled.h1`
  font-size: ${theme.fontSizes.x30};
  font-weight: ${theme.fontWeight.medium};
  line-height: ${theme.lineHeight.x38};
  color: ${theme.colors.brand700};
  margin: 0;
`;

const SubTitle = styled.h2`
  font-size: ${theme.fontSizes.x13};
  font-weight: ${theme.fontWeight.normal};
  line-height: ${theme.lineHeight.x20};
  color: ${theme.colors.gray500};
  margin: 0;
`;

const NavigationButton = styled(Button)`
  background: ${theme.colors.brand25};
  padding: ${theme.spaces.x12} ${theme.spaces.x16};
  border-radius: ${theme.radius.x8};
  cursor: pointer;

  width: fit-content;
  svg {
    color: ${theme.colors.brand700};
  }
  &:hover {
    background: ${theme.colors.brand50};
    border-color: ${theme.colors.brand300};
  }

  &:disabled {
    background: ${theme.colors.brand25};

    svg {
      color: ${theme.colors.brand300};
    }
  }
` as unknown as typeof Button;
const SubmitButton = styled(Button)`
  background: ${theme.colors.brand700};
` as unknown as typeof Button;
